import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Typography,
  Spin,
  message,
  Menu,
  Dropdown,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  LineChartOutlined,
  FileTextOutlined,
  DownloadOutlined,
  CarOutlined,
  ClusterOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import apiService from '../../services/api';

const { Title, Text } = Typography;

const RevenueReport = () => {
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState([]);

  useEffect(() => {
    fetchRevenueData();
  }, []);

  const fetchRevenueData = async () => {
    try {
      setLoading(true);
      const response = await apiService.reports.getRevenueReport({ format: 'json' });
      setRevenueData(response.data.data || []);
    } catch (err) {
      message.error('Failed to fetch revenue data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      setLoading(true);
      const response = await apiService.reports.getRevenueReport({ format });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `revenue_report.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success(`Report exported successfully as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting report:', error);
      message.error('Failed to export report');
    } finally {
      setLoading(false);
    }
  };

  const exportMenuItems = [
    {
      key: 'csv',
      icon: <FileTextOutlined />,
      label: 'Export as CSV',
      onClick: () => handleExport('csv')
    },
    {
      key: 'excel',
      icon: <FileTextOutlined />,
      label: 'Export as Excel',
      onClick: () => handleExport('excel')
    }
  ];

  const columns = [
    {
      title: 'Month',
      dataIndex: 'monthLabel',
      key: 'monthLabel',
      width: 150,
    },
    {
      title: 'Total Revenue',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      render: (value) => `£${Number(value).toFixed(2)}`,
      align: 'right',
      width: 150,
    },
    {
      title: 'Paid Revenue',
      dataIndex: 'paidRevenue',
      key: 'paidRevenue',
      render: (value) => `£${Number(value).toFixed(2)}`,
      align: 'right',
      width: 150,
    },
    {
      title: 'Unpaid Revenue',
      dataIndex: 'unpaidRevenue',
      key: 'unpaidRevenue',
      render: (value) => `£${Number(value).toFixed(2)}`,
      align: 'right',
      width: 150,
    },
    {
      title: 'Total Expense',
      dataIndex: 'totalExpense',
      key: 'totalExpense',
      render: (value) => `£${Number(value || 0).toFixed(2)}`,
      align: 'right',
      width: 150,
    },
    {
      title: 'Unpaid Expense',
      dataIndex: 'unpaidExpense',
      key: 'unpaidExpense',
      render: (value) => `£${Number(value || 0).toFixed(2)}`,
      align: 'right',
      width: 150,
    },
  ];

  // Summary stats from monthly data
  const monthCount = revenueData.length || 1;
  const totalRevenue = revenueData.reduce((sum, m) => sum + (m.totalRevenue || 0), 0);
  const totalExpense = revenueData.reduce((sum, m) => sum + (m.totalExpense || 0), 0);
  const totalNetIncome = totalRevenue - totalExpense;
  const avgRevenuePerMonth = totalRevenue / monthCount;
  const avgExpensePerMonth = totalExpense / monthCount;
  const avgNetIncomePerMonth = totalNetIncome / monthCount;
  const profitMargin = totalRevenue > 0 ? (totalNetIncome / totalRevenue) * 100 : 0;

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Revenue Report</Title>
        <Text type="secondary">Monthly revenue breakdown with paid and unpaid amounts</Text>
      </div>

      <Menu
        mode="horizontal"
        selectedKeys={['revenue']}
        style={{ marginBottom: '24px' }}
      >
        <Menu.Item key="dashboard" icon={<LineChartOutlined />}>
          <Link to="/reports">Dashboard</Link>
        </Menu.Item>
        <Menu.Item key="financial" icon={<span style={{ fontSize: '16px' }}>£</span>}>
          <Link to="/reports/financial">Financial</Link>
        </Menu.Item>
        <Menu.Item key="revenue" icon={<DollarOutlined />}>
          <Link to="/reports/revenue">Revenue</Link>
        </Menu.Item>
        <Menu.Item key="van" icon={<CarOutlined />}>
          <Link to="/reports/van">Vans</Link>
        </Menu.Item>
        <Menu.Item key="resources" icon={<ClusterOutlined />}>
          <Link to="/reports/resources">Resource Utilization</Link>
        </Menu.Item>
      </Menu>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic
              title="Avg Revenue / Month"
              value={avgRevenuePerMonth}
              precision={2}
              prefix="£"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic
              title="Avg Expense / Month"
              value={avgExpensePerMonth}
              precision={2}
              prefix="£"
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic
              title="Avg Net Income / Month"
              value={avgNetIncomePerMonth}
              precision={2}
              prefix="£"
              valueStyle={{ color: avgNetIncomePerMonth >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic
              title="Profit Margin"
              value={profitMargin}
              precision={1}
              suffix="%"
              valueStyle={{ color: profitMargin >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Monthly Revenue Breakdown"
        extra={
          <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
            <Button type="primary" icon={<DownloadOutlined />}>
              Export
            </Button>
          </Dropdown>
        }
      >
        <Table
          columns={columns}
          dataSource={revenueData}
          rowKey="month"
          pagination={{ pageSize: 12 }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Card title="Revenue Chart" style={{ marginTop: '24px' }}>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={revenueData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="monthLabel" angle={-45} textAnchor="end" height={100} />
            <YAxis />
            <Tooltip formatter={(value) => `£${Number(value).toFixed(2)}`} />
            <Legend />
            <Bar dataKey="totalRevenue" fill="#3f8600" name="Total Revenue" />
            <Bar dataKey="paidRevenue" fill="#52c41a" name="Paid Revenue" />
            <Bar dataKey="unpaidRevenue" fill="#ff4d4f" name="Unpaid Revenue" />
            <Bar dataKey="totalExpense" fill="#cf1322" name="Total Expense" />
            <Bar dataKey="unpaidExpense" fill="#ff7875" name="Unpaid Expense" />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
};

export default RevenueReport;
